import { WmDateRange, WmInput } from "@watermarkinsights/ripple-react";
import { forwardRef, useEffect, useImperativeHandle, useReducer } from "react";
import { useIntl } from "react-intl";
import { sendRequestWithPayload, sendRequest } from "@/common/http";
import { formatDateUsToIso } from "@/features/common/helper";
import { SettingsReportingYear } from "@/features/common/interfaces/Interface";

export interface CreateReportingYearFormProps {
  openReportingYearModalState: boolean;
  reportingYears: SettingsReportingYear[] | null;
  closeReportingYearModal: () => void;
  fetchReportingYears: () => void;
  updateReportingYear: SettingsReportingYear | null;
  addSnackBar: (reportingYearName: string, requestType: string) => void;
}

interface TokenResultSet {
  token: string;
}

export interface CreateReportingYearFormInstance {
  getCsrfToken: () => void;
}
interface fieldsErrorMessages {
  titleError: string;
  codeError: string;
  startDateError: string;
  endDateError: string;
}
export interface CreateReportingYearFormState {
  token?: string | null;
  reportingYearFields: reportingYearFields;
  fieldsErrorMessages: fieldsErrorMessages;
}

interface reportingYearFields {
  title: string;
  code: string;
  startDate: string;
  endDate: string;
  reportingYearType?: string;
}

const defaultFieldsErrorMessages = {
  titleError: "",
  codeError: "",
  startDateError: "",
  endDateError: ""
};

const initialState: CreateReportingYearFormState = {
  reportingYearFields: {
    title: "",
    code: "",
    startDate: "",
    endDate: "",
    reportingYearType: "academic"
  },
  fieldsErrorMessages: defaultFieldsErrorMessages,
  token: ""
};

interface Action {
  type: string;
  title: string;
  titleError: string;
  code: string;
  codeError: string;
  startDate: string;
  startDateError: string;
  endDate: string;
  endDateError: string;
  token: string | null;
  updateReportingYear: SettingsReportingYear | null;
}

const defaultAction: Action = {
  type: "",
  title: "",
  titleError: "",
  code: "",
  codeError: "",
  startDate: "",
  startDateError: "",
  endDate: "",
  endDateError: "",
  token: "",
  updateReportingYear: null
};

const formatDateIsoToUs = (date: string) => {
  if (!date) return "";
  // Check if already in US format (mm/dd/yyyy) - must not contain time component
  if (date.includes("/") && !date.includes("T")) return date;
  // Strip time component if present (e.g., "2024-01-01T00:00:00" -> "2024-01-01")
  const dateOnly = date.split("T")[0];
  // If it's already in US format after stripping time, return it
  if (dateOnly.includes("/")) return dateOnly;
  // Only convert if it's a valid ISO format (yyyy-mm-dd) with exactly 3 parts
  const d = dateOnly.split("-");
  if (d.length !== 3) {
    // Not a valid ISO date format, return as-is (user is still typing)
    return date;
  }
  // Convert from ISO format (yyyy-mm-dd) to US format (mm/dd/yyyy)
  return d[1] + "/" + d[2] + "/" + d[0];
};

const reducer = (state: CreateReportingYearFormState, action: Action) => {
  switch (action.type) {
    case "setTitle":
      return {
        ...state,
        reportingYearFields: {
          ...state.reportingYearFields,
          title: action.title
        },
        fieldsErrorMessages: {
          ...state.fieldsErrorMessages,
          titleError: ""
        }
      };
    case "setTitleError":
      return {
        ...state,
        fieldsErrorMessages: {
          ...state.fieldsErrorMessages,
          titleError: action.titleError
        }
      };
    case "setCode":
      return {
        ...state,
        reportingYearFields: {
          ...state.reportingYearFields,
          code: action.code
        },
        fieldsErrorMessages: {
          ...state.fieldsErrorMessages,
          codeError: ""
        }
      };
    case "setCodeError":
      return {
        ...state,
        fieldsErrorMessages: {
          ...state.fieldsErrorMessages,
          codeError: action.codeError
        }
      };
    case "setStartDate":
      return {
        ...state,
        reportingYearFields: {
          ...state.reportingYearFields,
          startDate: action.startDate
        },
        fieldsErrorMessages: {
          ...state.fieldsErrorMessages,
          startDateError: ""
        }
      };
    case "setStartDateError":
      return {
        ...state,
        fieldsErrorMessages: {
          ...state.fieldsErrorMessages,
          startDateError: action.startDateError
        }
      };
    case "setEndDate":
      return {
        ...state,
        reportingYearFields: {
          ...state.reportingYearFields,
          endDate: action.endDate
        },
        fieldsErrorMessages: {
          ...state.fieldsErrorMessages,
          endDateError: ""
        }
      };
    case "setEndDateError":
      return {
        ...state,
        fieldsErrorMessages: {
          ...state.fieldsErrorMessages,
          endDateError: action.endDateError
        }
      };
    case "resetFormInputs":
      return {
        reportingYearFields: {
          title: "",
          code: "",
          startDate: "",
          endDate: ""
        },
        fieldsErrorMessages: {
          titleError: "",
          codeError: "",
          startDateError: "",
          endDateError: ""
        }
      };

    case "setReportingYearFields":
      return {
        reportingYearFields: {
          title: action.updateReportingYear
            ? action.updateReportingYear.name
            : "",
          code: action.updateReportingYear
            ? action.updateReportingYear.code
            : "",
          startDate: action.updateReportingYear
            ? formatDateIsoToUs(action.updateReportingYear.startDate)
            : "",
          endDate: action.updateReportingYear
            ? formatDateIsoToUs(action.updateReportingYear.endDate)
            : ""
        },
        fieldsErrorMessages: {
          titleError: "",
          codeError: "",
          startDateError: "",
          endDateError: ""
        }
      };
    case "setToken":
      return {
        ...state,
        token: action.token
      };
    default:
      return state;
  }
};

const CreateReportingYearForm = forwardRef(
  (
    {
      reportingYears,
      openReportingYearModalState,
      closeReportingYearModal,
      fetchReportingYears,
      updateReportingYear,
      addSnackBar
    }: CreateReportingYearFormProps,
    ref
  ) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    useImperativeHandle(ref, () => ({
      getCsrfToken
    }));

    const { reportingYearFields, fieldsErrorMessages } = state;
    const titleLabel = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.titleLabel"
    });
    const startDateLabel = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.startDateLabel"
    });
    const endDateLabel = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.endDateLabel"
    });
    const codeLabel = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.codeLabel"
    });
    const titleInfo = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.titleInfo"
    });
    const codeInfo = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.codeInfo"
    });
    const titleRequired = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.titleRequired"
    });
    const uniqueTitle = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.uniqueTitle"
    });
    const codeRequired = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.codeRequired"
    });
    const uniqueCode = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.uniqueCode"
    });
    const dateRequired = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.dateRequired"
    });
    const invalidDate = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.invalidDate"
    });
    const overlappedDateError = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.overlappedDateError"
    });
    const startDateGreater = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.startDateGreater"
    });
    const endDateLesser = useIntl().formatMessage({
      id: "app.settings.reportingYearForm.endDateLesser"
    });

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({
        ...defaultAction,
        type: "setTitle",
        title: event.target.value
      });
    };

    const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({ ...defaultAction, type: "setCode", code: event.target.value });
    };
    const handleRangeSelection = async (event: any) => {
      const dateRange = event.target;
      const { startDate, endDate } = event.detail;

      resetOverlappedEndDateError();
      resetOverlappedStartDateError();

      const formattedStartDate = await dateRange.reformatDate(
        "ISO",
        "US",
        startDate.iso
      );
      const formattedEndDate = await dateRange.reformatDate(
        "ISO",
        "US",
        endDate.iso
      );

      dispatch({
        ...defaultAction,
        type: "setStartDate",
        startDate: formattedStartDate
      });

      dispatch({
        ...defaultAction,
        type: "setEndDate",
        endDate: formattedEndDate
      });

      // Validate the range after both dates are set
      validateReportingYearStartDate(formattedStartDate, formattedEndDate);
      validateReportingYearEndDate(formattedEndDate, formattedStartDate);
    };

    function validateReportingYearTitle(title: string) {
      if (title.trim() === "") {
        dispatch({
          ...defaultAction,
          type: "setTitleError",
          titleError: titleRequired
        });
      } else {
        const isDuplicate = reportingYears
          ?.filter((year) => year.uuid !== updateReportingYear?.uuid)
          .some(
            (item) =>
              item.name.toUpperCase().trim() === title.toUpperCase().trim()
          );
        if (isDuplicate) {
          dispatch({
            ...defaultAction,
            type: "setTitleError",
            titleError: uniqueTitle
          });
        }
      }
    }

    function validateReportingYearCode(code: string) {
      if (code.trim() === "") {
        dispatch({
          ...defaultAction,
          type: "setCodeError",
          codeError: codeRequired
        });
      } else {
        const isDuplicate = reportingYears
          ?.filter((year) => year.uuid !== updateReportingYear?.uuid)
          .some(
            (item) =>
              item.code.toUpperCase().trim() === code.toUpperCase().trim()
          );
        if (isDuplicate) {
          dispatch({
            ...defaultAction,
            type: "setCodeError",
            codeError: uniqueCode
          });
        }
      }
    }

    function validateReportingYearStartDate(
      startDate: string,
      endDate: string
    ) {
      if (fieldsErrorMessages.endDateError === endDateLesser) {
        dispatch({
          ...defaultAction,
          type: "setEndDateError",
          endDateError: ""
        });
      }
      if (startDate.trim() === "") {
        dispatch({
          ...defaultAction,
          type: "setStartDateError",
          startDateError: dateRequired
        });
      } else if (!isValidDateFormat(startDate)) {
        dispatch({
          ...defaultAction,
          type: "setStartDateError",
          startDateError: invalidDate
        });
      } else if (
        startDate &&
        endDate &&
        isValidDateFormat(endDate) &&
        isValidDateFormat(startDate) &&
        new Date(startDate) >= new Date(endDate)
      ) {
        dispatch({
          ...defaultAction,
          type: "setStartDateError",
          startDateError: startDateGreater
        });
      } else if (
        startDate &&
        endDate &&
        isValidDateFormat(endDate) &&
        isValidDateFormat(startDate) &&
        reportingYears
          ?.filter((year) => year.uuid !== updateReportingYear?.uuid)
          .some((year) =>
            isDateRangeOverlap(year.startDate, year.endDate, startDate, endDate)
          )
      ) {
        dispatch({
          ...defaultAction,
          type: "setStartDateError",
          startDateError: overlappedDateError
        });
        dispatch({
          ...defaultAction,
          type: "setEndDateError",
          endDateError: overlappedDateError
        });
      }
    }
    function validateReportingYearEndDate(endDate: string, startDate: string) {
      if (fieldsErrorMessages.startDateError === startDateGreater) {
        dispatch({
          ...defaultAction,
          type: "setStartDateError",
          startDateError: ""
        });
      }
      if (endDate.trim() === "") {
        dispatch({
          ...defaultAction,
          type: "setEndDateError",
          endDateError: dateRequired
        });
      } else if (!isValidDateFormat(endDate)) {
        dispatch({
          ...defaultAction,
          type: "setEndDateError",
          endDateError: invalidDate
        });
      } else if (
        startDate &&
        endDate &&
        isValidDateFormat(endDate) &&
        isValidDateFormat(startDate) &&
        new Date(startDate) >= new Date(endDate)
      ) {
        dispatch({
          ...defaultAction,
          type: "setEndDateError",
          endDateError: endDateLesser
        });
      } else if (
        startDate &&
        endDate &&
        isValidDateFormat(endDate) &&
        isValidDateFormat(startDate) &&
        reportingYears
          ?.filter((year) => year.uuid !== updateReportingYear?.uuid)
          .some((year) =>
            isDateRangeOverlap(year.startDate, year.endDate, startDate, endDate)
          )
      ) {
        dispatch({
          ...defaultAction,
          type: "setEndDateError",
          endDateError: overlappedDateError
        });
        dispatch({
          ...defaultAction,
          type: "setStartDateError",
          startDateError: overlappedDateError
        });
      }
    }

    const handleTitleBlur = () => {
      validateReportingYearTitle(reportingYearFields.title);
    };
    const handleCodeBlur = () => {
      validateReportingYearCode(reportingYearFields.code);
    };

    function resetOverlappedEndDateError() {
      if (state.fieldsErrorMessages.endDateError === overlappedDateError) {
        dispatch({
          ...defaultAction,
          type: "setEndDateError",
          endDateError: ""
        });
      }
    }
    function resetOverlappedStartDateError() {
      if (state.fieldsErrorMessages.startDateError === overlappedDateError) {
        dispatch({
          ...defaultAction,
          type: "setStartDateError",
          startDateError: ""
        });
      }
    }

    const handleStartDateBlur = (event: any) => {
      // Get the current value from the component
      const dateRangeEl = event.target;
      const currentStartValue = dateRangeEl.valueStart || dateRangeEl["value-start"] || "";
      
      // Update state with the current value
      if (currentStartValue !== reportingYearFields.startDate) {
        dispatch({
          ...defaultAction,
          type: "setStartDate",
          startDate: currentStartValue
        });
      }
      
      resetOverlappedEndDateError();
      validateReportingYearStartDate(
        currentStartValue,
        reportingYearFields.endDate
      );
    };

    const handleEndDateBlur = (event: any) => {
      // Get the current value from the component
      const dateRangeEl = event.target;
      const currentEndValue = dateRangeEl.valueEnd || dateRangeEl["value-end"] || "";
      
      // Update state with the current value
      if (currentEndValue !== reportingYearFields.endDate) {
        dispatch({
          ...defaultAction,
          type: "setEndDate",
          endDate: currentEndValue
        });
      }
      
      resetOverlappedStartDateError();
      validateReportingYearEndDate(
        currentEndValue,
        reportingYearFields.startDate
      );
    };

    const isDateRangeOverlap = (
      existingStartDate: string,
      existingEndDate: string,
      newStartDate: string,
      newEndDate: string
    ) => {
      const existingStart = new Date(existingStartDate).getTime();
      const existingEnd = new Date(existingEndDate).getTime();
      const newStart = new Date(newStartDate).getTime();
      const newEnd = new Date(newEndDate).getTime();

      return (
        (existingStart <= newStart && newStart <= existingEnd) ||
        (existingStart <= newEnd && newEnd <= existingEnd) ||
        (newStart <= existingStart && existingStart <= newEnd) ||
        (newStart <= existingEnd && existingEnd <= newEnd)
      );
    };

    const isValidDateFormat = (dateString: string) => {
      const pattern = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
      return pattern.test(dateString);
    };

    useEffect(() => {
      dispatch({ ...defaultAction, type: "resetFormInputs" });
    }, [openReportingYearModalState]);

    useEffect(() => {
      dispatch({
        ...defaultAction,
        type: "setReportingYearFields",
        updateReportingYear: updateReportingYear
      });
    }, [updateReportingYear]);

    function validateAllFields() {
      if (reportingYearFields.title.trim() === "") {
        dispatch({
          ...defaultAction,
          type: "setTitleError",
          titleError: titleRequired
        });
      }
      if (reportingYearFields.code.trim() === "") {
        dispatch({
          ...defaultAction,
          type: "setCodeError",
          codeError: codeRequired
        });
      }
      if (reportingYearFields.startDate.trim() === "") {
        dispatch({
          ...defaultAction,
          type: "setStartDateError",
          startDateError: dateRequired
        });
      }
      if (reportingYearFields.endDate.trim() === "") {
        dispatch({
          ...defaultAction,
          type: "setEndDateError",
          endDateError: dateRequired
        });
      }
      return (
        reportingYearFields.title &&
        reportingYearFields.code &&
        reportingYearFields.startDate &&
        reportingYearFields.endDate &&
        !fieldsErrorMessages.titleError &&
        !fieldsErrorMessages.codeError &&
        !fieldsErrorMessages.startDateError &&
        !fieldsErrorMessages.endDateError
      );
    }

    function getCsrfToken() {
      if (validateAllFields()) {
        const applyResultToken = (result: TokenResultSet) => {
          dispatch({ ...defaultAction, type: "setToken", token: result.token });
        };
        const onError = (error: string) => {
          // Log error in console for debugging
          console.log("API Error:", error);
          dispatch({ ...defaultAction, type: "setToken", token: null });
        };

        sendRequest(
          {
            uri: "/export/token",
            camelize: true
          },
          applyResultToken,
          onError
        );
      } else {
        if (
          fieldsErrorMessages.titleError ||
          reportingYearFields.title.trim() === ""
        ) {
          focusField("#title-input");
        } else if (
          fieldsErrorMessages.codeError ||
          reportingYearFields.code.trim() === ""
        ) {
          focusField("#code-input");
        } else if (
          fieldsErrorMessages.startDateError ||
          reportingYearFields.startDate === ""
        ) {
          focusField("#date-range-input");
        } else if (
          fieldsErrorMessages.endDateError ||
          reportingYearFields.endDate === ""
        ) {
          focusField("#date-range-input");
        }
      }
    }

    function createUpdateReportingYear() {
      let formData = new FormData();
      formData.append("name", reportingYearFields.title);
      formData.append("code", reportingYearFields.code);
      if (reportingYearFields.reportingYearType) {
        formData.append("type", reportingYearFields.reportingYearType);
      }
      formData.append(
        "start_date",
        formatDateUsToIso(reportingYearFields.startDate)
      );
      formData.append(
        "end_date",
        formatDateUsToIso(reportingYearFields.endDate)
      );
      formData.append("_csrf_token", state.token ? state.token : "");
      const applyResult = () => {
        closeReportingYearModal();
        fetchReportingYears();
        setTimeout(function () {
          updateReportingYear
            ? addSnackBar(reportingYearFields.title, "update")
            : addSnackBar(reportingYearFields.title, "create");
        }, 1000);
      };

      let ryCreateUpdateUrl = "/sa/reporting_years";
      if (updateReportingYear) {
        ryCreateUpdateUrl = ryCreateUpdateUrl + `/${updateReportingYear.uuid}`;
      }
      sendRequestWithPayload(
        {
          uri: ryCreateUpdateUrl,
          body: formData
        },
        applyResult
      );
    }

    function focusField(elementId: string) {
      let inputElement = document.querySelector(elementId);
      setTimeout(function () {
        (inputElement as HTMLInputElement).focus();
      }, 300);
    }

    useEffect(() => {
      if (state.token) {
        createUpdateReportingYear();
      }
    }, [state.token]);

    return (
      <>
        <div className="input-margin">
          <WmInput
            label={titleLabel}
            type="text"
            value={reportingYearFields.title}
            required-field={true}
            error-message={fieldsErrorMessages.titleError}
            info={fieldsErrorMessages.titleError ? "" : titleInfo}
            onInput={(e) => handleTitleChange(e)}
            onBlur={handleTitleBlur}
            character-limit={50}
            id="titleinput"
          ></WmInput>
        </div>
        <div className="input-margin">
          <WmInput
            label={codeLabel}
            value={reportingYearFields.code}
            type="text"
            required-field={true}
            error-message={fieldsErrorMessages.codeError}
            info={fieldsErrorMessages.codeError ? "" : codeInfo}
            onInput={(e) => handleCodeChange(e)}
            onBlur={handleCodeBlur}
            character-limit={25}
            id="code-input"
          ></WmInput>
        </div>
        <div className="date-inputs">
          <WmDateRange
            date-format="mm/dd/yyyy"
            label-start={startDateLabel}
            label-end={endDateLabel}
            required-field={true}
            value-start={formatDateIsoToUs(reportingYearFields.startDate)}
            value-end={formatDateIsoToUs(reportingYearFields.endDate)}
            error-message={
              fieldsErrorMessages.startDateError ||
              fieldsErrorMessages.endDateError
            }
            invalid-start={!!fieldsErrorMessages.startDateError}
            invalid-end={!!fieldsErrorMessages.endDateError}
            onInput={(e: any) => {
              // Get the current values from the component using camelCase property names
              const dateRangeEl = e.target;
              const startValue = dateRangeEl.valueStart || "";
              const endValue = dateRangeEl.valueEnd || "";

              // Update state as user types for responsive feedback
              dispatch({
                ...defaultAction,
                type: "setStartDate",
                startDate: startValue
              });
              dispatch({
                ...defaultAction,
                type: "setEndDate",
                endDate: endValue
              });
            }}
            onWmStartInputBlur={handleStartDateBlur}
            onWmEndInputBlur={handleEndDateBlur}
            onWmRangeSelection={handleRangeSelection}
            id="date-range-input"
          ></WmDateRange>
        </div>
      </>
    );
  }
);
export default CreateReportingYearForm;
